import type { LangCode } from "@/lib/types";

/**
 * Translated article bodies, by locale then post slug — loaded one locale at a
 * time, on demand.
 *
 * Separate from BLOG_TRANSLATIONS (title/excerpt/category) so a locale's bodies
 * can land independently: bodies are the bulk of the content and are being
 * translated locale by locale. A slug absent here simply keeps the English
 * body, via getLocalizedPost()'s `translation.body || post.body` fallback.
 *
 * Why dynamic imports rather than the static ones this file used to hold: the
 * nine modules are 984 KB of article prose, and blog.ts imported them at module
 * scope to merge them into BLOG_POSTS. That made them a dependency of every
 * importer of blog.ts — including BlogWidget, which the home page renders — so
 * `/` shipped a 1.08 MB modulepreload (334 KB brotli) of every article in every
 * language to show three headlines. Now the home page loads none of it, and an
 * article page loads only the locale it is being read in. English pays nothing
 * either way: English bodies live in blog.ts itself.
 */
const BODY_MODULES: Partial<Record<LangCode, () => Promise<Record<string, string>>>> = {
  en: () => import("./en").then((m) => m.EN_BODIES),
  tr: () => import("./tr").then((m) => m.TR_BODIES),
  ko: () => import("./ko").then((m) => m.KO_BODIES),
  ja: () => import("./ja").then((m) => m.JA_BODIES),
  de: () => import("./de").then((m) => m.DE_BODIES),
  es: () => import("./es").then((m) => m.ES_BODIES),
  ru: () => import("./ru").then((m) => m.RU_BODIES),
  ar: () => import("./ar").then((m) => m.AR_BODIES),
  zh: () => import("./zh").then((m) => m.ZH_BODIES),
  fa: () => import("./fa").then((m) => m.FA_BODIES),
  fr: () => import("./fr").then((m) => m.FR_BODIES),
};

/** Resolved locales, so a second article in the same locale costs nothing.
 *  Bodies are immutable build-time content, so this is safe to share across
 *  requests on the server. */
const loaded = new Map<LangCode, Record<string, string>>();

/** The bodies for one locale. Empty for English and for any locale with no
 *  body module yet — both mean "use the English body". */
export async function loadBlogBodies(lang: LangCode): Promise<Record<string, string>> {
  const cached = loaded.get(lang);
  if (cached) return cached;
  const load = BODY_MODULES[lang];
  if (!load) return {};
  const bodies = await load();
  loaded.set(lang, bodies);
  return bodies;
}

/** Already-resolved bodies, without triggering a load. For render paths that
 *  cannot await — they render the English body until a loadBlogBodies() call
 *  elsewhere has populated the cache. */
export function peekBlogBodies(lang: LangCode): Record<string, string> | undefined {
  return loaded.get(lang);
}
